import { throttle } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { RESIZE_THROTTLE_MS } from '../../constants/duration';
import { Nullable } from '../../types/Nullable';
import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import {
  computeTotalLines,
  deriveLineState,
  nextVisibleLines,
  parseLineHeight,
} from './useShowMoreLines.utils';

interface Props {
  /** Number of lines initially visible. Pass undefined to disable this hook. */
  limitLines: number | undefined;
  /** Indicates if Show More / Show Less should display as text or an icon button */
  mode?: 'text' | 'icon';
  /** Number of additional lines to reveal per click (defaults to limitLines) */
  stepLines?: number;
  /** Override label for "Show less" */
  showLessText?: string;
  /** Override label for "Show more" */
  showMoreText?: string;
}

const resolveLineHeight = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element);
  return parseLineHeight(style.lineHeight, style.fontSize);
};

export const useShowMoreLines = ({
  limitLines,
  mode = 'text',
  stepLines = limitLines ?? 1,
  showLessText,
  showMoreText,
}: Props): {
  clampStyle: React.CSSProperties | undefined;
  handleShowLess: () => void;
  handleShowMore: () => void;
  shouldShowMore: boolean;
  showMore: Nullable<JSX.Element>;
  textRef: React.RefObject<HTMLDivElement>;
} => {
  const intl = useIntl();
  const textRef = useRef<HTMLDivElement>(null);
  const [visibleLines, setVisibleLines] = useState(limitLines ?? 0);
  const [totalLines, setTotalLines] = useState(0);

  useEffect(() => {
    setVisibleLines(limitLines ?? 0);
  }, [limitLines]);

  const measure = useCallback(() => {
    const element = textRef.current;
    if (!element) return;
    const lh = resolveLineHeight(element);
    if (lh === 0) return;
    setTotalLines(computeTotalLines(element.scrollHeight, lh));
  }, []);

  useEffect(() => {
    const element = textRef.current;
    if (!element || limitLines === undefined) return;

    const throttledMeasure = throttle(measure, RESIZE_THROTTLE_MS, {
      leading: true,
      trailing: true,
    });
    const observer = new ResizeObserver(throttledMeasure);
    observer.observe(element);

    return () => {
      observer.disconnect();
      throttledMeasure.cancel();
    };
  }, [limitLines, measure]);

  const { hasHiddenLines, shouldShowMore } = deriveLineState({
    limitLines,
    visibleLines,
    totalLines,
  });

  const handleShowMore = useCallback(() => {
    setVisibleLines((prev) => nextVisibleLines(prev, stepLines, totalLines));
  }, [stepLines, totalLines]);

  const handleShowLess = useCallback(() => {
    setVisibleLines(limitLines ?? 0);
  }, [limitLines]);

  const clampStyle: React.CSSProperties | undefined = shouldShowMore
    ? {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: visibleLines,
        overflow: 'hidden',
      }
    : undefined;

  const showMoreButtonText =
    showMoreText ??
    intl.formatMessage({
      id: 'UseShowMoreLines.showMore',
      defaultMessage: 'Show more',
      description: 'Label for showing more lines of text',
    });

  const showLessButtonText =
    showLessText ??
    intl.formatMessage({
      id: 'UseShowMoreLines.showLess',
      defaultMessage: 'Show less',
      description: 'Label for showing fewer lines of text',
    });

  const showMore: Nullable<JSX.Element> = useMemo(() => {
    if (!hasHiddenLines) {
      return null;
    }

    if (mode === 'icon') {
      return (
        <IconButton
          preset={shouldShowMore ? 'show' : 'hide'}
          onClick={shouldShowMore ? handleShowMore : handleShowLess}
        />
      );
    }

    return (
      <Button
        isInline
        PRIVATE_isPadded={false}
        text={shouldShowMore ? showMoreButtonText : showLessButtonText}
        variant="action"
        onClick={shouldShowMore ? handleShowMore : handleShowLess}
      />
    );
  }, [
    hasHiddenLines,
    mode,
    shouldShowMore,
    showMoreButtonText,
    showLessButtonText,
    handleShowMore,
    handleShowLess,
  ]);

  return {
    clampStyle,
    handleShowLess,
    handleShowMore,
    shouldShowMore,
    showMore,
    textRef,
  };
};
